import React from 'react';
import { testimonials, videoTestimonials } from '../data/mockData';
import { Star, Quote, Play, Instagram } from 'lucide-react';

const Testimonials = () => {
  return (
    <div className="min-h-screen pt-24 bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Client Testimonials
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Real experiences from our satisfied customers across Karnataka and Goa
          </p>
        </div>
      </section>

      {/* Overall Rating */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl p-12 text-center shadow-2xl">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-12 h-12 text-yellow-400 fill-yellow-400 mx-1" />
              ))}
            </div>
            <h2 className="text-6xl font-bold text-[#004534] mb-4">5.0 / 5.0</h2>
            <p className="text-2xl text-gray-700 font-medium mb-2">
              Excellent Customer Satisfaction
            </p>
            <p className="text-lg text-gray-600">
              Based on 5000+ completed projects and client feedback
            </p>
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Video Testimonials
            </h2>
            <p className="text-xl text-gray-600 flex items-center justify-center space-x-2">
              <Instagram className="w-6 h-6 text-pink-600" />
              <span>Watch our customer stories on Instagram</span>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {videoTestimonials.map((video, index) => (
              <a
                key={video.id}
                href={video.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                      <Play className="w-10 h-10 text-[#004534] fill-[#004534]" />
                    </div>
                  </div>
                  {/* Instagram Badge */}
                  <div className="absolute top-4 right-4 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 text-white px-3 py-2 rounded-full flex items-center space-x-2 shadow-lg">
                    <Instagram className="w-4 h-4" />
                    <span className="text-xs font-semibold">Watch Reel</span>
                  </div>
                  {/* Location Badge */}
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                    {video.location}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#004534] mb-2 group-hover:text-[#0C6951] transition-colors">
                    {video.name}
                  </h3>
                  <p className="text-sm font-semibold text-[#0C6951] mb-3">
                    {video.title}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {video.description}
                  </p>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://www.instagram.com/shri_durga_enterprises_/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 hover:from-purple-700 hover:via-pink-700 hover:to-orange-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <Instagram className="w-6 h-6" />
              <span>Follow Us on Instagram</span>
            </a>
          </div>
        </div>
      </section>

      {/* Text Testimonials */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Written Reviews
            </h2>
            <p className="text-xl text-gray-600">
              Hear directly from our satisfied customers
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 relative animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 opacity-10">
                  <Quote className="w-24 h-24 text-[#1e3a8a]" />
                </div>

                <div className="relative z-10">
                  {/* Client Info */}
                  <div className="flex items-center mb-6">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-20 h-20 rounded-full object-cover mr-4 border-4 border-[#22c55e] shadow-lg"
                    />
                    <div>
                      <h3 className="text-2xl font-bold text-[#004534]">{testimonial.name}</h3>
                      <p className="text-sm text-gray-600 font-medium">{testimonial.role}</p>
                      <p className="text-sm text-gray-500">{testimonial.company}</p>
                      <p className="text-xs text-gray-400 mt-1">{testimonial.location}</p>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-gray-700 text-lg leading-relaxed italic">
                    "{testimonial.text}"
                  </p>

                  {/* Decorative Element */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center text-sm text-gray-500">
                      <div className="w-2 h-2 bg-[#22c55e] rounded-full mr-2"></div>
                      <span>Verified Client</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Trusted by All Sectors
            </h2>
            <p className="text-xl text-gray-600">
              Our diverse client base speaks to our versatility and expertise
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { title: 'Homeowners', count: '2500+' },
              { title: 'Industries', count: '800+' },
              { title: 'Hotels', count: '400+' },
              { title: 'Apartments', count: '600+' },
              { title: 'Businesses', count: '200+' },
            ].map((category, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <h3 className="text-3xl font-bold text-[#004534] mb-2">{category.count}</h3>
                <p className="text-gray-700 font-medium">{category.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Why Clients Choose Us
            </h2>
            <p className="text-xl text-gray-600">
              Proven track record of excellence and customer satisfaction
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                metric: '100%',
                label: 'Project Success Rate',
                description: 'Every project completed on time',
              },
              {
                metric: '98%',
                label: 'Customer Retention',
                description: 'Clients come back for more',
              },
              {
                metric: '24/7',
                label: 'Support Available',
                description: 'Always there when you need us',
              },
              {
                metric: '5★',
                label: 'Average Rating',
                description: 'Consistently top-rated service',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-5xl font-bold text-[#004534] mb-3">{item.metric}</div>
                <h3 className="text-xl font-semibold text-[#0C6951] mb-2">{item.label}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our Happy Clients
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Experience the same quality and service that our 5000+ clients have trusted
          </p>
          <button className="inline-flex items-center space-x-2 bg-[#D3FF62] hover:bg-[#c4f052] text-[#004534] px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105 shadow-2xl">
            <span>Start Your Solar Journey</span>
          </button>
        </div>
      </section>

      <style jsx>{`
        .solar-grid-bg {
          background-image: 
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
          background-size: 80px 80px;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default Testimonials;
